<template>
  <div class="carousel-outer">
    <!-- Background vignette -->
    <div class="vignette" aria-hidden="true"></div>

    <!-- Floating gold particles -->
    <div class="particles" aria-hidden="true">
      <span
        v-for="n in 22"
        :key="n"
        class="particle"
        :style="{
          left: `${(n * 41 + 7) % 100}%`,
          top: `${(n * 59 + 13) % 100}%`,
          animationDelay: `${(n * 0.6) % 5}s`,
          animationDuration: `${4 + (n % 4)}s`,
          width: `${2 + (n % 3)}px`,
          height: `${2 + (n % 3)}px`,
        }"
      ></span>
    </div>

    <swiper
      ref="mySwiper"
      :modules="modules"
      :lazy="true"
      :slides-per-view="2"
      :slidesPerGroup="2"
      :space-between="20"
      :grid="{ rows: 3, fill: 'row' }"
      :navigation="false"
      :autoplay="{ delay: 10000, disableOnInteraction: false }"
      :scrollbar="{ draggable: true }"
      @swiper="onSwiper"
    >
      <swiper-slide
        v-for="(item, index) in sortedItems"
        :key="index"
        class="auction-item"
        :class="{ 'is-placeholder': item.isPlaceholder }"
      >
        <template v-if="!item.isPlaceholder">
          <!-- Gold accent bars -->
          <span class="accent-bar accent-bar--left" aria-hidden="true"></span>
          <span class="accent-bar accent-bar--right" aria-hidden="true"></span>

          <!-- Title bar at top of card -->
          <div class="title-bar">
            <div class="itemname">{{ item.name }}</div>
            <div v-if="item.creator" class="itemcreator">
              {{ item.creator }}
            </div>
          </div>

          <div class="item-inner">
            <!-- Image -->
            <div class="image-wrap">
              <img
                :src="item.resizedImages[1]?.url"
                :alt="item.name"
                loading="lazy"
                class="image"
              />
              <div
                class="swiper-lazy-preloader swiper-lazy-preloader-white"
              ></div>
            </div>

            <!-- Item number in center column -->
            <div class="iteminfo">
              <div class="lot-badge">
                <span class="lot-number">{{ item.itemNumber }}</span>
              </div>
            </div>

            <!-- Bid column -->
            <div class="bids">
              <div class="price-display-tv">
                <div class="price-label">
                  <template v-if="item.isDonationItem && !item.latestBid">
                    Donation Item
                  </template>
                  <template v-else-if="item.isDonationItem && item.latestBid">
                    Latest Donation
                  </template>
                  <template v-else-if="item.liveAuction">
                    Live Auction
                  </template>
                  <template v-else-if="item.latestBidder">
                    Highest Bid
                  </template>
                  <template v-else>
                    Starting Bid
                  </template>
                </div>
                <div class="price-value">
                  <PriceDisplay
                    :price="getDisplayedBid(item)"
                    :currency="item.currency"
                  />
                </div>
                <div class="divider"></div>
                <div class="biddername">
                  <template v-if="item.latestBidder">
                    {{ item.latestBidder }}
                  </template>
                  <template v-else>&nbsp;</template>
                </div>
              </div>
            </div>
          </div>
        </template>
      </swiper-slide>
    </swiper>

    <div class="bidticker">
      <Vue3Marquee :duration="marqueeDuration">
        <span
          v-for="(donation, index) in donations"
          :key="index"
          class="donation"
          v-html="donation.message"
        ></span>
      </Vue3Marquee>
    </div>

    <Notification
      :message="currentNotification"
      :show="showNotification"
      @hidden="handleNotificationHidden"
    />
  </div>
</template>

<script>
import { Vue3Marquee } from "vue3-marquee";
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import { db } from "@/firebase";
import {
  collection,
  collectionGroup,
  onSnapshot,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import Notification from "@/views/display/components/Notification.vue";
import PriceDisplay from "@/views/auction/components/DisplayPrice.vue";
import { formatPrice } from "@/services/formatPrice";

import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Grid,
  Autoplay,
} from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/pagination";

export default {
  name: "AuctionSlider",
  components: {
    Notification,
    PriceDisplay,
    Swiper,
    SwiperSlide,
    Vue3Marquee,
  },
  setup() {
    const swiperInstance = ref(null);
    const onSwiper = (swiper) => {
      swiperInstance.value = swiper;
    };

    const items = ref([]);
    const donations = ref([]);
    const notificationQueue = ref([]);
    const currentNotification = ref("");
    const showNotification = ref(false);
    const donationsLoaded = ref(false);
    const QUEUE_LIMIT = 100;
    const donationContainer = ref(null);
    const animationDuration = ref(40);

    // Capture the timestamp when the page is loaded
    const pageLoadTimestamp = new Date();

    // To track processed bids/donations to avoid duplication
    const processedBids = new Set();
    const processedDonationDocs = new Set();
    let bidsListenerActive = false;
    let donationsListenerActive = false;

    const fetchItems = async () => {
      const q = query(collection(db, "auctionItems"), orderBy("itemNumber"));

      onSnapshot(q, (querySnapshot) => {
        const updatedItems = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((item) => !item.isSponsorDefault);

        items.value = updatedItems;

        listenForBids();
        listenForDonations();
      });
    };

    // Efficiently listen for new bids using server-side filtering
    const listenForBids = () => {
      if (bidsListenerActive) return;
      bidsListenerActive = true;
      const bidsRef = collectionGroup(db, "bids");
      const bidsQuery = query(
        bidsRef,
        where("timestamp", ">", pageLoadTimestamp),
        orderBy("timestamp", "desc")
      );

      onSnapshot(bidsQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const newBid = change.doc.data();

            if (!processedBids.has(newBid.autoBidId)) {
              processedBids.add(newBid.autoBidId);

              const itemIndex = items.value.findIndex(
                (i) => i.id === newBid.itemID
              );

              if (itemIndex !== -1) {
                items.value[itemIndex] = {
                  ...items.value[itemIndex],
                  latestBid: newBid.userBid,
                  latestBidder: newBid.user.name,
                };

                const formattedPrice = formatPrice(
                  newBid.userBid,
                  newBid.currency || "SEK"
                );

                const notificationMessage = `
              <h2>#${newBid.itemNumber} - ${newBid.itemName}</h2>
              <h3>New bid of <span class="stronger">${formattedPrice}</span> placed by ${newBid.user.name}</h3>
            `;

                notificationQueue.value.push(notificationMessage);

                if (notificationQueue.value.length > QUEUE_LIMIT) {
                  notificationQueue.value.shift();
                }

                nextTick(() => {
                  currentNotification.value = notificationQueue.value[0];
                  showNotification.value = true;
                });
              }
            }
          }
        });
      });
    };

    // Track which donation items already have purchase listeners
    const listenedDonationItems = new Set();

    const setupPurchaseListener = (itemId) => {
      if (listenedDonationItems.has(itemId)) return;
      listenedDonationItems.add(itemId);

      const purchasesRef = collection(
        db,
        `auctionItems/${itemId}/purchase`
      );
      const purchasesQuery = query(
        purchasesRef,
        orderBy("timestamp", "desc")
      );

      onSnapshot(purchasesQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const purchaseDocId = change.doc.id;

            // Skip if we already processed this document
            if (processedDonationDocs.has(purchaseDocId)) return;
            processedDonationDocs.add(purchaseDocId);

            const newDonation = change.doc.data();

            const formattedDonation = formatPrice(
              newDonation.userBid,
              newDonation.currency || "SEK"
            );

            donations.value.push({
              id: purchaseDocId,
              message: `Donation of ${formattedDonation} by ${newDonation.user.name}`,
            });

            if (donations.value.length > QUEUE_LIMIT) {
              donations.value.shift();
            }

            // Show notification popup for donations added after page load
            const donationTime = newDonation.timestamp?.toDate
              ? newDonation.timestamp.toDate()
              : new Date((newDonation.timestamp?.seconds || 0) * 1000);
            if (donationTime > pageLoadTimestamp) {
              const notificationMessage = `
                <h2>NEW DONATION!</h2>
                <h3><span class="stronger">${formattedDonation}</span> by ${newDonation.user.name}</h3>
              `;
              notificationQueue.value.push(notificationMessage);

              if (notificationQueue.value.length > QUEUE_LIMIT) {
                notificationQueue.value.shift();
              }

              nextTick(() => {
                currentNotification.value = notificationQueue.value[0];
                showNotification.value = true;
              });
            }

            nextTick(() => {
              calculateAnimationDuration();
            });
          }
        });

        donationsLoaded.value = true;
      });
    };

    const listenForDonations = () => {
      if (donationsListenerActive) return;
      donationsListenerActive = true;

      const donationItemsQuery = query(
        collection(db, "auctionItems"),
        where("isDonationItem", "==", true)
      );

      // Use onSnapshot instead of getDocs so new donation items are picked up in real-time
      onSnapshot(donationItemsQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            setupPurchaseListener(change.doc.id);
          }
        });
      });
    };

    const calculateAnimationDuration = () => {
      const container = donationContainer.value;
      if (container) {
        const containerWidth = container.scrollWidth;
        const speed = 100;
        animationDuration.value = containerWidth / speed;
      }
    };

    const marqueeDuration = computed(() => {
      const baseDuration = 40;
      const additionalTime = 5 * Math.max(donations.value.length - 1, 0);
      return baseDuration + additionalTime;
    });

    const handleResize = () => {
      if (swiperInstance.value) {
        swiperInstance.value.update();
      }
    };

    onMounted(async () => {
      await fetchItems();
      calculateAnimationDuration();
      window.addEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", handleResize);
    });

    const getDisplayedBid = (item) => {
      if (item.isDonationItem) {
        return item.latestBid || item.startbid;
      }
      return item.latestBid || item.startbid;
    };

    const sortedItems = computed(() => {
      const sorted = [...items.value].sort(
        (a, b) => a.itemNumber - b.itemNumber
      );
      const itemsPerSlide = 3;

      const emptyItemsCount =
        (itemsPerSlide - (sorted.length % itemsPerSlide)) % itemsPerSlide;

      for (let i = 0; i < emptyItemsCount; i++) {
        sorted.push({ isPlaceholder: true });
      }

      return sorted;
    });

    watch(notificationQueue, (newQueue) => {
      if (
        newQueue.length > 0 &&
        (!currentNotification.value ||
          currentNotification.value === newQueue[0])
      ) {
        currentNotification.value = newQueue[0];
        showNotification.value = true;
      }
    });

    const handleNotificationHidden = () => {
      showNotification.value = false;
      notificationQueue.value.shift();
      if (notificationQueue.value.length > 0) {
        setTimeout(() => {
          currentNotification.value = notificationQueue.value[0];
          showNotification.value = true;
        }, 500);
      } else {
        currentNotification.value = "";
        showNotification.value = false;
      }
    };

    return {
      sortedItems,
      getDisplayedBid,
      currentNotification,
      showNotification,
      notificationQueue,
      handleNotificationHidden,
      donations,
      donationContainer,
      animationDuration,
      donationsLoaded,
      marqueeDuration,
      onSwiper,
      modules: [Navigation, Pagination, Scrollbar, A11y, Grid, Autoplay],
    };
  },
};
</script>

<style>
html,
body,
#app {
  height: 100% !important;
  width: 100% !important;
  margin: 0;
  padding: 0;
}

/* ── Outer shell ───────────────────────────────────────────────── */
.carousel-outer {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background:
    radial-gradient(ellipse at top, #0f1a3d 0%, #07102a 55%, #030612 100%);
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
  color: #fff;
}

.vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at center,
    transparent 55%,
    rgba(0, 0, 0, 0.55) 100%
  );
  pointer-events: none;
  z-index: 0;
}

/* ── Particles ─────────────────────────────────────────────────── */
.particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
.particle {
  position: absolute;
  border-radius: 50%;
  background: #c39935;
  box-shadow: 0 0 6px #c39935;
  animation: float-p linear infinite;
}
@keyframes float-p {
  0%,
  100% {
    transform: translateY(0) scale(1);
    opacity: 0.25;
  }
  50% {
    transform: translateY(-40px) scale(1.4);
    opacity: 0.75;
  }
}

/* ── Swiper / slide ────────────────────────────────────────────── */
.swiper {
  width: 100%;
  flex: 1;
  min-height: 0;
  margin: 0 auto;
  padding: 24px 24px 12px;
  position: relative;
  z-index: 1;
}
.swiper-wrapper {
  margin-top: 0 !important;
}
.swiper-slide {
  padding: 0 12px;
  box-sizing: border-box;
}

/* ── Auction item card ─────────────────────────────────────────── */
.auction-item {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 14px 28px 18px;
  border-radius: 16px;
  border: 1px solid rgba(195, 153, 53, 0.3);
  background: linear-gradient(
    135deg,
    rgba(195, 153, 53, 0.08) 0%,
    rgba(255, 255, 255, 0.03) 100%
  );
  box-shadow:
    0 6px 24px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  overflow: hidden;
  color: #fff;
}
.auction-item.is-placeholder {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  backdrop-filter: none;
}

/* Symmetric gold accent bars */
.accent-bar {
  position: absolute;
  top: 14%;
  bottom: 14%;
  width: 4px;
  background: linear-gradient(180deg, #e4c066, #c39935 50%, #8a6b17);
  box-shadow: 0 0 12px rgba(195, 153, 53, 0.6);
  pointer-events: none;
}
.accent-bar--left {
  left: 0;
  border-radius: 0 4px 4px 0;
}
.accent-bar--right {
  right: 0;
  border-radius: 4px 0 0 4px;
}

/* Inner layout: image | info | bid */
.item-inner {
  display: flex;
  align-items: stretch;
  gap: 24px;
  flex: 1;
  min-height: 0;
}
.image-wrap {
  flex: 0 0 34%;
  max-width: 34%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 0;
}
.iteminfo {
  flex: 1 1 32%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  padding: 0 8px;
}
.bids {
  flex: 0 0 34%;
  max-width: 34%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Image ─────────────────────────────────────────────────────── */
.image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border: 2px solid rgba(195, 153, 53, 0.55);
  border-radius: 10px;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
}

/* ── Title bar (top of card) ───────────────────────────────────── */
.title-bar {
  align-self: center;
  text-align: center;
  max-width: 92%;
  padding: 2px 12px 4px;
  margin-bottom: 6px;
}
.title-bar .itemname {
  font-family: "Playfair Display", serif;
  font-size: clamp(1.3rem, 1.7vw, 2rem);
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
  letter-spacing: 0.01em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(195, 153, 53, 0.25);
}
.title-bar .itemcreator {
  font-family: "Playfair Display", serif;
  font-style: italic;
  font-size: clamp(0.95rem, 1.1vw, 1.3rem);
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.2;
  margin-top: 6px;
}

/* ── Item number badge (centered in middle column) ────────────── */
.lot-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 90px;
  padding: 10px 22px;
  border: 2px solid rgba(195, 153, 53, 0.55);
  border-radius: 16px;
  background: rgba(195, 153, 53, 0.1);
  box-shadow:
    0 4px 18px rgba(0, 0, 0, 0.35),
    0 0 22px rgba(195, 153, 53, 0.18);
  line-height: 1;
}
.lot-number {
  font-family: "Playfair Display", serif;
  font-size: clamp(2.4rem, 3vw, 3.6rem);
  font-weight: 700;
  color: #e4c066;
  line-height: 1;
  /* Use lining figures so all digits align to cap-height.
     Without this, Playfair's oldstyle figures (3, 5, 7, 9) hang
     below baseline and look vertically off-center in the badge. */
  font-variant-numeric: lining-nums;
  font-feature-settings: "lnum" 1;
  text-shadow: 0 0 22px rgba(195, 153, 53, 0.7);
}

.itemname {
  font-family: "Playfair Display", serif;
  font-size: clamp(1.2rem, 1.5vw, 1.75rem);
  font-weight: 600;
  color: #fff;
  line-height: 1.25;
  letter-spacing: 0.01em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  /* clamp to 3 lines for tidy layout */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.itemcreator {
  font-family: "Playfair Display", serif;
  font-style: italic;
  font-size: clamp(0.95rem, 1.1vw, 1.3rem);
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.2;
}

/* ── Bid / price block ─────────────────────────────────────────── */
.price-display-tv {
  width: 100%;
  /* Keep the bid card from getting too wide on large TVs —
     scales with viewport but caps out so it doesn't sprawl. */
  max-width: clamp(240px, 19vw, 360px);
  text-align: center;
  padding: 14px 16px;
  border: 1px solid rgba(195, 153, 53, 0.35);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.25);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.price-label {
  font-size: clamp(0.75rem, 0.9vw, 1rem);
  font-weight: 700;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(195, 153, 53, 0.85);
  margin-bottom: 6px;
}
.price-value {
  font-family: "Playfair Display", serif;
  font-size: clamp(1.8rem, 2.4vw, 3rem);
  font-weight: 700;
  line-height: 1.15;
  padding-bottom: 0.08em;
  background: linear-gradient(
    135deg,
    #fff4d1 0%,
    #e4c066 40%,
    #c39935 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 14px rgba(195, 153, 53, 0.35));
  animation: shimmer 6s ease-in-out infinite;
  white-space: nowrap;
}
.price-value :deep(small) {
  /* Keep currency visible over the transparent gradient */
  -webkit-text-fill-color: #c39935;
  color: #c39935;
  background: none;
  font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.4em;
  font-weight: 700;
  letter-spacing: 0.2em;
  margin-left: 0.3em;
  opacity: 0.95;
  vertical-align: 0.15em;
}
@keyframes shimmer {
  0%,
  100% {
    background-position: 0% 0%;
  }
  50% {
    background-position: 100% 0%;
  }
}

.divider {
  display: block;
  width: 60%;
  height: 1px;
  margin: 10px auto;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(195, 153, 53, 0.55),
    transparent
  );
  border: none;
}
.biddername {
  font-size: clamp(1rem, 1.2vw, 1.4rem);
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.02em;
  min-height: 1.4em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Notification helper class ─────────────────────────────────── */
.stronger {
  font-weight: bold;
  color: #c39935;
}

/* ── Donation ticker (bottom) ──────────────────────────────────── */
.bidticker {
  width: 100%;
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  white-space: nowrap;
  position: relative;
  z-index: 1;
  background:
    linear-gradient(90deg, #0a1336 0%, #0f1a3d 50%, #0a1336 100%);
  border-top: 1px solid rgba(195, 153, 53, 0.45);
  box-shadow:
    0 -2px 18px rgba(195, 153, 53, 0.15),
    inset 0 1px 0 rgba(195, 153, 53, 0.2);
}
.donation {
  display: inline-block;
  margin-right: 80px;
  font-family: "Playfair Display", serif;
  font-size: 1.6rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  background: linear-gradient(
    135deg,
    #fff4d1 0%,
    #e4c066 40%,
    #c39935 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 10px rgba(195, 153, 53, 0.3));
}
.donation :deep(small) {
  color: #c39935;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-left: 0.2em;
  font-size: 0.7em;
}

/* ── Legacy transition names (kept) ────────────────────────────── */
.bounce-enter-active,
.bounce-leave-active {
  transition: opacity 0.5s;
}
.bounce-enter,
.bounce-leave-to {
  opacity: 0;
}
</style>
